import React, { useCallback, } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../app/store";
import { cn } from "../../../../../lib/utils";
import { DraftFMR1Fields, selectFMR1DraftFmrFields, updateFMR1DraftField } from "../../../logic/frm1Slice";


const FMR1SignatoryInfoForm: React.FC = () => {
  const { id: case_id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const draftFields = useAppSelector(selectFMR1DraftFmrFields);

  const handleFieldChange = useCallback((key: keyof DraftFMR1Fields, value: any) => {
    if (!case_id) return;
    dispatch(updateFMR1DraftField({ key, value }));
  }, [case_id, dispatch]);

  const renderHeaderRow = () => (
    <div className="flex w-full border-b border-[#E5E5E5]">
      <div className="w-[270px] min-w-[85px] h-11 px-2 py-1.5 flex items-center bg-[#F1F5F9]">
        <span className="font-sans font-medium text-sm leading-5 text-[#737373]">Particulars</span>
      </div>
      <div className="flex-1 h-11 p-2 flex items-center bg-[#F1F5F9]">
        <span className="font-sans font-medium text-sm leading-5 text-[#737373]">Authorised Reporting Official</span>
      </div>
      <div className="flex-1 h-11 p-2 flex items-center bg-[#F1F5F9]">
        <span className="font-sans font-medium text-sm leading-5 text-[#737373]">Countersigned By</span>
      </div>
    </div>
  );

  const renderTextInput = (
    value: string | undefined,
    onValueChange: (v: string) => void,
    placeholder: string,
    inputDisabled: boolean,
  ) => (
    <input
      value={value ?? ""}
      disabled={inputDisabled}
      onChange={(e) => onValueChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full flex-1 bg-transparent",
        "font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
        "focus:outline-none",
      )}
    />
  );

  const renderDateInput = (value: string | undefined, onValueChange: (v: string) => void, inputDisabled: boolean) => (
    <input
      type="date"
      value={value ?? ""}
      disabled={inputDisabled}
      onChange={(e) => onValueChange(e.target.value)}
      className={cn(
        "w-full bg-transparent",
        "font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
        "focus:outline-none",
        inputDisabled && "opacity-50 cursor-not-allowed",
      )}
    />
  );

  const renderRow = (
    label: React.ReactNode,
    leftKey: keyof DraftFMR1Fields,
    rightKey: keyof DraftFMR1Fields,
    opts?: {
      type?: "text" | "date";
      placeholderLeft?: string;
      placeholderRight?: string;
    },
  ) => {
    const type = opts?.type ?? "text";
    return (
      <div className="flex w-full border-b border-[#E5E5E5]">
        <div className="w-[270px] min-w-[85px] h-11 px-2 py-1.5 flex items-center bg-[#F1F5F9]">
          <span className="font-sans font-medium text-sm leading-5 text-[#0A0A0A]">{label}</span>
        </div>
        <div className="flex-1 h-11 p-2 flex items-center bg-white">
          {type === "date"
            ? renderDateInput(draftFields[leftKey] as any, (v) => handleFieldChange(leftKey, v), false)
            : renderTextInput(
                draftFields[leftKey] as any,
                (v) => handleFieldChange(leftKey, v),
                opts?.placeholderLeft ?? "",
                false,
              )}
        </div>
        <div className="flex-1 h-11 p-2 flex items-center bg-white">
          {type === "date"
            ? renderDateInput(draftFields[rightKey] as any, (v) => handleFieldChange(rightKey, v), false)
            : renderTextInput(
                draftFields[rightKey] as any,
                (v) => handleFieldChange(rightKey, v),
                opts?.placeholderRight ?? "",
                false,
              )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="w-full overflow-visible">
        <div className="border border-[#E5E5E5] overflow-visible">
          {renderHeaderRow()}
          {renderRow(
            <>
              Name <span className="text-red-500">**</span>
            </>,
            "authorised_reporting_official_name",
            "countersigned_by_name",
            {
              placeholderLeft: "Enter name",
              placeholderRight: "Enter name",
            },
          )}
          {renderRow(
            <>
              Designation <span className="text-red-500">**</span>
            </>,
            "authorised_reporting_official_designation",
            "countersigned_by_designation",
            {
              placeholderLeft: "Enter designation",
              placeholderRight: "Enter designation",
            },
          )}
          {renderRow(
            <>
              E-mail ID <span className="text-red-500">**</span>
            </>,
            "authorised_reporting_official_email_id",
            "countersigned_by_email_id",
            {
              placeholderLeft: "Enter email",
              placeholderRight: "Enter email",
            },
          )}
          {renderRow(
            <>
              Tel. No. (O) [ With STD Code] <span className="text-red-500">**</span>
            </>,
            "authorised_reporting_official_tel_o_std",
            "countersigned_by_tel_o_std",
            {
              placeholderLeft: "Enter telephone no.",
              placeholderRight: "Enter telephone no.",
            },
          )}
          {renderRow(
            "Tel. No. (R)  [ With STD Code]",
            "authorised_reporting_official_tel_r_std",
            "countersigned_by_tel_r_std",
            {
              placeholderLeft: "Enter telephone no.",
              placeholderRight: "Enter telephone no.",
            },
          )}
          {renderRow("Place", "authorised_reporting_official_place", "countersigned_by_place", {
            placeholderLeft: "Enter place",
            placeholderRight: "Enter place",
          })}
          {renderRow("Date", "authorised_reporting_official_date", "countersigned_by_date", { type: "date" })}
        </div>
      </div>
    </>
  );
};

FMR1SignatoryInfoForm.displayName = "FMR1SignatoryInfoForm";

export default FMR1SignatoryInfoForm;
